import React from "react";
import { useDispatch, useSelector } from "react-redux";
import DragTool from "../../components/drag-tool";
import { RiBallPenLine } from "react-icons/ri";
import { setActiveCoordinate } from "../../redux/slices/activeCoordinateReducer";
import {
  updateCoordinatePosition,
  updateCoordinateSize,
} from "../../utils/updateCoordinates";
import { setSignatureCoordinates } from "../../redux/slices/signatureCoordinatesReducer";

const SignatureCoordinatesContainer = ({ scrollContainer }) => {
  const dispatch = useDispatch();
  const supportedFields = useSelector((state) => state.supportedFields.supportedFields);
  const activePageNumber = useSelector((state) => state.activePageNumber.activePageNumber);
  const activeCoordinate = useSelector((state) => state.activeCoordinate.activeCoordinate);
  const signatoryList = useSelector((state) => state.signatoryList.signatoryList);
  const signatureCoordinates = useSelector((state) => state.signatureCoordinates.signatureCoordinates);
  const textBoxCoordinates = useSelector((state) => state.textBoxCoordinates.textBoxCoordinates);
  const dateCoordinates = useSelector((state) => state.dateCoordinates.dateCoordinates);
  const checkBoxCoordinates = useSelector((state) => state.checkBoxCoordinates.checkBoxCoordinates);
  const emailBoxCoordinates = useSelector((state) => state.emailBoxCoordinatesReducer?.emailBoxCoordinates || []);
  const picklistCoordinates = useSelector((state) => state.picklistCoordinates.picklistCoordinates);
  const pageDimensions = useSelector((state) => state.generalDocument?.pageDimensions);

  const allCoordinates = React.useMemo(
    () => [...signatureCoordinates.map(c => ({ ...c, _isSignature: true })), ...textBoxCoordinates, ...dateCoordinates, ...checkBoxCoordinates, ...emailBoxCoordinates, ...picklistCoordinates],
    [signatureCoordinates, textBoxCoordinates, dateCoordinates, checkBoxCoordinates, emailBoxCoordinates, picklistCoordinates]
  );

  const handleDragStart = (id) => {
    const signatureCoordinate = signatureCoordinates.find((ele) => ele.eleId === id);
    if (signatureCoordinate) {
      dispatch(
        setActiveCoordinate({
          activeCoordinate: signatureCoordinate,
          isSelectionMode: true,
        })
      );
    }
  };

  const handleDragStop = (id, data) => {
    if (!data || typeof data.x !== "number" || typeof data.y !== "number") return;
    const updatedCoords = updateCoordinatePosition(signatureCoordinates, id, data.x, data.y, pageDimensions);
    dispatch(setSignatureCoordinates({ signatureCoordinates: updatedCoords }));
  };

  const handleResize = (id, data) => {
    if (!data || typeof data.width !== "number" || typeof data.height !== "number") return;
    const updatedCoords = updateCoordinateSize(signatureCoordinates, id, data.width, data.height, data.x, data.y, "Signature", pageDimensions);
    dispatch(setSignatureCoordinates({ signatureCoordinates: updatedCoords }));
  };

  return signatureCoordinates.map((ele) => {
    if (ele.pageId !== activePageNumber) return null;

    const toolName = "Signature";
    const bgColor = ele.bgColor;
    const signatory = signatoryList.find((s) => s.id === ele.signatory_id);
    const signatoryLabel = signatory?.label || `Signer ${(ele.user_index_id ?? 0) + 1}`;
    const field = supportedFields.find((field) => field.type === toolName);

    return (
      <DragTool
        key={ele.eleId}
        isEditMode={activeCoordinate?.eleId === ele.eleId}
        bounds={scrollContainer}
        bgColor={bgColor}
        toolType={toolName}
        eleId={ele.eleId}
        pageId={ele.pageId}
        x={ele.x}
        y={ele.y}
        width={ele.width}
        height={ele.height}
        minWidth={field?.minWidth}
        minHeight={field?.minHeight}
        maxWidth={field?.maxWidth}
        maxHeight={field?.maxHeight}
        handleDragStart={() => handleDragStart(ele.eleId)}
        handleDragStop={(e, d) => handleDragStop(ele.eleId, d)}
        handleResize={(data, e) => handleResize(ele.eleId, data)}
        allCoordinates={allCoordinates.filter((c) => c.pageId === ele.pageId)}
        signatoryLabel={signatoryLabel}
        // toolIcon={<RiBallPenLine className={`${toolName.toLowerCase()}-tool-icon-position`} />}
        toolIcon={
          <div style={{
            display: "flex",
            flexDirection: "column",
            // alignItems: "center",
            // justifyContent: "center",
            padding: "3px",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            // overflow: "visible",
            // position: "relative",
          }}>
            {/* Top label - outside box above */}
            <span style={{
              position: "absolute",
              top: "-18px",
              left: 0,
              fontSize: "10px",
              fontWeight: 500,
              color: "#444",
              letterSpacing: "0.4px",
              lineHeight: 1,
              userSelect: "none",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              backgroundColor: "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              padding: "1px 5px",
              borderRadius: "2px",
              textShadow: "0 0 3px rgba(255,255,255,1)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            }}>
              Signed On: DD/MM/YYYY
            </span>

            {/* Center icon */}
            <RiBallPenLine className={`${toolName.toLowerCase()}-tool-icon-position`} />

            {/* Bottom label - outside box below */}
            <span style={{
              position: "absolute",
              bottom: "-18px",
              left: 0,
              fontSize: "10px",
              fontWeight: 500,
              color: "#444",
              letterSpacing: "0.4px",
              lineHeight: 1,
              userSelect: "none",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              backgroundColor: "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              padding: "1px 5px",
              borderRadius: "2px",
              textShadow: "0 0 3px rgba(255,255,255,1)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            }}>
              Signed By: Signer Name
            </span>
          </div>
        }
      />
    );
  });
};

export default SignatureCoordinatesContainer;